import classes from "./StudioGallery.module.css"
import { StudioCarousel } from "./StudioCarousel";
import { useState, useEffect, useCallback, useLayoutEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { getUrl } from "aws-amplify/storage";
import awsConfig from "../../../config/aws-config";
import {
    getDirectS3StudioImageUrl,
    getCloudFrontOriginalImageUrl,
    normalizePublicImageKey,
} from "../../../util/cloudFrontUtils";

/** Mirrors breakpoints in StudioGallery.module.css so only one grid mounts (one request per URL). */
function useGalleryLayout() {
    const [layout, setLayout] = useState("3col");
    useLayoutEffect(() => {
        const read = () => {
            if (window.matchMedia("(max-width: 399px)").matches) return "1col";
            if (window.matchMedia("(max-width: 1059px)").matches) return "2col";
            return "3col";
        };
        const apply = () => setLayout(read());
        apply();
        const mq399 = window.matchMedia("(max-width: 399px)");
        const mq1059 = window.matchMedia("(max-width: 1059px)");
        mq399.addEventListener("change", apply);
        mq1059.addEventListener("change", apply);
        return () => {
            mq399.removeEventListener("change", apply);
            mq1059.removeEventListener("change", apply);
        };
    }, []);
    return layout;
}

/** After a tile enters the scroll area, hint a few early loads (still gated by IntersectionObserver). */
const GALLERY_HIGH_FETCHPRIORITY_COUNT = 8;

/** Prefetch slightly before tiles enter the gallery scrollport (single observer per tile; cheap at ~50 tiles). */
const GALLERY_IO_ROOT_MARGIN = "200px 0px 320px 0px";

const CLOUDFRONT_ORIGIN = "https://d23shqpev1klq3.cloudfront.net";

/** Amplify getUrl in one huge Promise.all can throttle; smaller waves keep fallbacks arriving steadily. */
const PRESIGN_BATCH_SIZE = 5;
const PRESIGN_BATCH_GAP_MS = 100;

const gallerySuccessfulSrcByAttemptKey = new Map();

function stripGalleryRetryParams(url) {
    if (!url || typeof url !== "string") return "";
    try {
        const parsed = new URL(url);
        parsed.searchParams.delete("_sgretry");
        parsed.searchParams.delete("_sgtier");
        parsed.searchParams.delete("_sgrecovery");
        return parsed.toString();
    } catch {
        return url;
    }
}

/**
 * net::ERR_BLOCKED_BY_ORB: Chromium refuses to give <img> a cross-origin body that is not a real image
 * (e.g. CloudFront/Lambda returns application/json with {"message":"Internal server error"}).
 * This fetch (when CORS allows) surfaces status, content-type, and a short body preview.
 */
function logFailedImageUrlDiagnostic(url, context) {
    if (typeof url !== "string" || !url) return;
    void (async () => {
        try {
            const res = await fetch(url, { method: "GET", mode: "cors", credentials: "omit" });
            const ct = res.headers.get("content-type") || "";
            const snippet = (await res.clone().text()).slice(0, 400);
            console.warn("[StudioGallery] failed URL — fetch diagnostic (ORB = non-image response blocked for <img>)", {
                ...context,
                status: res.status,
                contentType: ct,
                bodyPreview: snippet,
            });
        } catch (e) {
            console.warn("[StudioGallery] failed URL — could not fetch for diagnostic (CORS or network)", {
                ...context,
                error: String(e?.message || e),
                hint: "In Network: open the failed request → Headers (status, content-type) + Response. JSON/XML error bodies trigger ORB, not a decoding bug in React.",
            });
        }
    })();
}

function GalleryGridSkeleton({ galleryLayout, imageCount, containerClassName, scrollRootRef }) {
    const cols = galleryLayout === "3col" ? 3 : galleryLayout === "2col" ? 2 : 1;
    if (!imageCount) return null;
    return (
        <div
            className={containerClassName}
            ref={scrollRootRef}
            aria-busy="true"
            aria-label="Galerie wird vorbereitet"
        >
            {Array.from({ length: cols }, (_, columnIndex) => (
                <div key={columnIndex} className={classes.imageColumn}>
                    {Array.from(
                        { length: Math.max(0, Math.ceil((imageCount - columnIndex) / cols)) },
                        (_, rowInCol) => {
                            const gi = rowInCol * cols + columnIndex;
                            return (
                                <div
                                    key={gi}
                                    className={`${classes.imageShell} ${classes.imageShellLoading}`}
                                >
                                    <span className={classes.skeletonThumb} aria-hidden />
                                </div>
                            );
                        }
                    )}
                </div>
            ))}
        </div>
    );
}

function GalleryImage({
    attemptUrls,
    globalIndex,
    scrollRootRef,
    alt,
    onImageClick,
    className,
}) {
    const ioTargetRef = useRef(null);
    const imgRef = useRef(null);
    const [inView, setInView] = useState(false);
    const [phase, setPhase] = useState("loading");
    const attemptUrlsRef = useRef(attemptUrls);
    attemptUrlsRef.current = attemptUrls;
    const [displaySrc, setDisplaySrc] = useState(() => attemptUrls[0] || "");
    const retryTimerRef = useRef(null);
    /** Index into attemptUrls (0 = first URL to try, usually presigned S3 when logged in). */
    const loadTierRef = useRef(0);

    const attemptKey = attemptUrls.join("\u0001");
    const getInitialDisplaySrc = useCallback(() => {
        const cachedSuccess = gallerySuccessfulSrcByAttemptKey.get(attemptKey);
        if (cachedSuccess) {
            const normalizedCached = stripGalleryRetryParams(cachedSuccess);
            if (attemptUrls.some((u) => stripGalleryRetryParams(u) === normalizedCached)) {
                return cachedSuccess;
            }
        }
        return attemptUrls[0] || "";
    }, [attemptKey, attemptUrls]);

    useEffect(() => {
        if (!attemptUrls.length) {
            setPhase("error");
            setDisplaySrc("");
            return undefined;
        }
        setPhase("loading");
        setDisplaySrc(getInitialDisplaySrc());
        const normalizedDisplay = stripGalleryRetryParams(getInitialDisplaySrc());
        const resolvedTier = attemptUrls.findIndex((u) => stripGalleryRetryParams(u) === normalizedDisplay);
        loadTierRef.current = resolvedTier >= 0 ? resolvedTier : 0;
        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [attemptKey, attemptUrls, getInitialDisplaySrc]);

    useEffect(() => {
        const el = ioTargetRef.current;
        const root = scrollRootRef?.current;
        if (!el) return undefined;
        if (!root) {
            setInView(true);
            return undefined;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                const hit = entries.some((e) => e.isIntersecting);
                if (hit) {
                    setInView(true);
                    obs.disconnect();
                }
            },
            { root, rootMargin: GALLERY_IO_ROOT_MARGIN, threshold: 0.01 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [attemptKey, scrollRootRef]);

    const handleLoad = () => {
        const successfulSrc = stripGalleryRetryParams(imgRef.current?.currentSrc || displaySrc);
        if (successfulSrc) gallerySuccessfulSrcByAttemptKey.set(attemptKey, successfulSrc);
        setPhase("loaded");
    };

    /** Same as StudioCarousel: on error, try the next URL tier immediately (e.g. WebP → passthrough JPEG). */
    const handleError = useCallback(
        (e) => {
            const el = e.currentTarget;
            const urls = attemptUrlsRef.current;
            const tier = loadTierRef.current;
            const nextTier = tier + 1;

            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }

            if (nextTier < urls.length) {
                loadTierRef.current = nextTier;
                const nextUrl = urls[nextTier];
                const sep = nextUrl.includes("?") ? "&" : "?";
                setDisplaySrc(`${nextUrl}${sep}_sgtier=${nextTier}_${Date.now()}`);
                setPhase("loading");
                return;
            }

            setPhase("error");
            console.warn("[StudioGallery] image failed (all URL tiers exhausted)", {
                globalIndex,
                lastSrc: el.currentSrc,
                attemptCount: urls.length,
                failedAtTier: tier,
            });
            logFailedImageUrlDiagnostic(el.currentSrc, { globalIndex });
        },
        [globalIndex]
    );

    const isLoading = phase === "loading";
    const isError = phase === "error";
    const showLoadingChrome = isLoading;

    /** Cached images often complete before React's onLoad; thumbnails stayed opacity:0 forever. */
    useLayoutEffect(() => {
        if (!inView || isError) return undefined;
        const img = imgRef.current;
        if (img?.complete && img.naturalWidth > 0) {
            setPhase("loaded");
        }
        return undefined;
    }, [displaySrc, isError, inView]);

    const shellClass = isError
        ? `${classes.imageShell} ${classes.imageShellError}`
        : `${classes.imageShell} ${showLoadingChrome || !inView ? classes.imageShellLoading : classes.imageShellLoaded}`;

    const showSkeleton = !isError && !inView;

    return (
        <div
            ref={ioTargetRef}
            className={shellClass}
            onClick={isError ? () => onImageClick(globalIndex) : undefined}
            role={isError ? "button" : undefined}
            tabIndex={isError ? 0 : undefined}
            onKeyDown={
                isError
                    ? (ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                              ev.preventDefault();
                              onImageClick(globalIndex);
                          }
                      }
                    : undefined
            }
        >
            {(showLoadingChrome || isError) && !showSkeleton && (
                <span className={classes.imageShellPlaceholder} aria-hidden />
            )}
            {showSkeleton && <span className={classes.skeletonThumb} aria-hidden />}
            {!isError && inView && displaySrc && (
                <img
                    ref={imgRef}
                    key={displaySrc}
                    src={displaySrc}
                    alt={alt}
                    loading="eager"
                    decoding="async"
                    {...(inView && globalIndex < GALLERY_HIGH_FETCHPRIORITY_COUNT
                        ? { fetchpriority: "high" }
                        : {})}
                    onClick={() => onImageClick(globalIndex)}
                    className={`${className} ${isLoading ? classes.galleryImageHidden : classes.galleryImageVisible}`}
                    onLoad={handleLoad}
                    onError={handleError}
                />
            )}
        </div>
    );
}

export const StudioGallery = ({ studioName, images, imageKeys, onClose }) => {
    const [showCarousel, setShowCarousel] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [carouselStartIndex, setCarouselStartIndex] = useState(0);
    const galleryLayout = useGalleryLayout();
    const isAuthenticated = Boolean(useSelector((state) => state?.auth?.user?.id));

    const keysFingerprint = useMemo(
        () => (imageKeys?.length ? imageKeys.join("\u0001") : ""),
        [imageKeys]
    );

    const directS3Fallbacks = useMemo(() => {
        if (!imageKeys?.length) return [];
        return imageKeys.map((key) => (key ? getDirectS3StudioImageUrl(key) : ""));
    }, [imageKeys]);

    const cloudFrontPassthroughUrls = useMemo(() => {
        if (!imageKeys?.length) return [];
        return imageKeys.map((key) => (key ? getCloudFrontOriginalImageUrl(key) : ""));
    }, [imageKeys]);

    /** Same as FotosEditor: signed URLs work when the bucket is not public; guests keep unsigned public URLs. */
    const [presignedFallbacks, setPresignedFallbacks] = useState(null);

    useEffect(() => {
        if (!isAuthenticated || !imageKeys?.length) {
            setPresignedFallbacks(null);
            return undefined;
        }
        let cancelled = false;
        setPresignedFallbacks(null);
        (async () => {
            const urls = new Array(imageKeys.length);
            for (let i = 0; i < imageKeys.length; i += PRESIGN_BATCH_SIZE) {
                if (cancelled) return;
                const batch = imageKeys.slice(i, i + PRESIGN_BATCH_SIZE);
                const batchUrls = await Promise.all(
                    batch.map(async (key) => {
                        if (!key) return "";
                        const storagePath = normalizePublicImageKey(key);
                        if (!storagePath) return "";
                        try {
                            const result = await getUrl({
                                path: storagePath,
                                options: { validateObjectExistence: false },
                            });
                            return result.url.toString();
                        } catch {
                            try {
                                const result = await getUrl({
                                    key: storagePath,
                                    options: {
                                        accessLevel: "public",
                                        validateObjectExistence: false,
                                    },
                                });
                                return result.url.toString();
                            } catch {
                                return getDirectS3StudioImageUrl(key);
                            }
                        }
                    })
                );
                batchUrls.forEach((u, j) => {
                    urls[i + j] = u;
                });
                if (i + PRESIGN_BATCH_SIZE < imageKeys.length && !cancelled) {
                    await new Promise((r) => setTimeout(r, PRESIGN_BATCH_GAP_MS));
                }
            }
            if (!cancelled) setPresignedFallbacks(urls);
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, keysFingerprint, imageKeys]);

    const s3FallbackUrls = presignedFallbacks ?? directS3Fallbacks;

    /** Shared source order for both grid and fullscreen carousel. */
    const sharedAttemptUrlsByIndex = useMemo(() => {
        if (!images?.length) return [];
        return images.map((cfResize, i) => {
            const pre =
                isAuthenticated && Array.isArray(presignedFallbacks)
                    ? presignedFallbacks[i] || ""
                    : "";
            const pass = cloudFrontPassthroughUrls[i] || "";
            const s3u = s3FallbackUrls[i] || "";
            const ordered = [];
            if (cfResize) ordered.push(cfResize);
            if (pre) ordered.push(pre);
            if (pass) ordered.push(pass);
            if (s3u) ordered.push(s3u);
            return [...new Set(ordered)].filter(Boolean);
        });
    }, [
        images,
        cloudFrontPassthroughUrls,
        s3FallbackUrls,
        isAuthenticated,
        presignedFallbacks,
    ]);

    const attemptUrlsByIndex = sharedAttemptUrlsByIndex;
    const carouselAttemptUrlsByIndex = sharedAttemptUrlsByIndex;

    const authWaitingPresign =
        Boolean(isAuthenticated && imageKeys?.length && presignedFallbacks === null);

    const scrollRootRef = useRef(null);

    /** Warm TLS + DNS to CloudFront and S3 when the modal opens (cheap; browser dedupes duplicate hints). */
    useEffect(() => {
        const s3Origin = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com`;
        const hrefs = [CLOUDFRONT_ORIGIN, s3Origin];
        const created = [];
        hrefs.forEach((href) => {
            const link = document.createElement("link");
            link.rel = "preconnect";
            link.href = href;
            link.crossOrigin = "anonymous";
            document.head.appendChild(link);
            created.push(link);
        });
        return () => {
            created.forEach((el) => el.parentNode?.removeChild(el));
        };
    }, []);

    // Prevent background scroll while gallery is open (iOS Safari safe)
    useEffect(() => {
        const scrollY = window.scrollY || window.pageYOffset;
        const previousBodyStyle = document.body.style.cssText;
        const previousHtmlStyle = document.documentElement.style.cssText;

        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.height = '100%';

        return () => {
            document.body.style.cssText = previousBodyStyle;
            document.documentElement.style.cssText = previousHtmlStyle;
            window.scrollTo(0, scrollY);
        };
    }, []);

    // No history push here; Studio.jsx owns the ?gallery=open URL state.

    const handleClose = useCallback(() => {
        setIsFadingOut(true);
    }, []);

    const handleImageClick = (index) => {
        setCarouselStartIndex(index);
        setShowCarousel(true);
        // updateUrlWithState('carousel', true);
    };


    useEffect(() => {
        if (isFadingOut) {
          const timer = setTimeout(() => {
            onClose();
          }, 10);
    
          return () => clearTimeout(timer);
        }
    }, [isFadingOut, onClose]);     

  return (
    <div 
        className={`${classes.mainContainer} ${isFadingOut ? classes.fadeOut : ''}`}
        style={{ height: showCarousel ? "100svh" : "100%" }}
    >
        <header>
            <div onClick={handleClose} className={classes.closeButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0 9.12355L8.99855 0.125L10.2006 1.32708L2.35773 9.16998L10.2486 17.7184L8.99941 18.8715L0.0172915 9.14084L0 9.12355Z" fill="#1F1F1F"/>
                </svg>
                <span className="mediumLarge">Zurück</span>
            </div>
            <span className={classes.studioName}>{studioName}</span>
            {/* <div className={classes.extrasContainer}>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 15 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.2583 0L10.9611 3.71119L9.95414 4.70343L7.95893 2.70368V12.5396H6.54092V2.71625L4.54493 4.7119L3.53923 3.71844L7.2583 0ZM2.41092 7.55162C1.86699 7.55162 1.418 7.993 1.418 8.55279V17.0896C1.418 17.6494 1.86699 18.0908 2.41092 18.0908H12.0891C12.633 18.0908 13.082 17.6494 13.082 17.0896V8.55279C13.082 7.993 12.633 7.55162 12.0891 7.55162H9.72052V6.14239H12.0891C13.4275 6.14239 14.5 7.226 14.5 8.55279V17.0896C14.5 18.4164 13.4275 19.5 12.0891 19.5H2.41092C1.07254 19.5 0 18.4164 0 17.0896V8.55279C0 7.226 1.07254 6.14239 2.41092 6.14239H4.77948V7.55162H2.41092Z" fill="#1F1F1F"/>
                    </svg>
                    <span>Teilen</span>
                </div>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="17" viewBox="0 0 20 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M2.32105 3.22796L2.31969 3.22999C1.74451 4.0868 1.70646 5.05679 1.87393 5.92884C1.95647 6.35863 2.08365 6.73029 2.1912 6.99445C2.24443 7.1252 2.29119 7.22557 2.3223 7.2889C2.33614 7.31705 2.34551 7.33529 2.35075 7.34529C2.88265 8.22447 3.35801 8.88959 3.84416 9.46027C4.53318 10.2665 6.24335 12.0186 9.7831 14.4136C13.0873 12.1666 14.9441 10.3266 15.7637 9.4205C16.1011 9.04478 16.6181 8.43061 17.1875 7.40185C17.2635 7.25208 17.5349 6.68233 17.6788 5.92569C17.8485 5.03299 17.8078 4.06252 17.2489 3.22999C16.6077 2.27486 15.4716 1.69385 14.4014 1.71957C13.678 1.74226 12.8985 2.03917 12.0718 2.76285L12.0668 2.76714C11.8241 2.97781 11.6106 3.20935 11.4333 3.45442L11.4307 3.45814L9.79988 5.69863L8.14661 3.47302C7.94518 3.20155 7.73221 2.97226 7.49628 2.76783L7.4925 2.76455C6.66932 2.04645 5.89447 1.74686 5.17775 1.72015H5.07775C4.04335 1.72015 2.94187 2.29989 2.32105 3.22796ZM5.20306 0.220151C6.3468 0.25457 7.45272 0.733308 8.48933 1.63759C8.60017 1.73363 8.70689 1.8334 8.81 1.93753C9.00586 2.13533 9.18871 2.34887 9.3621 2.58255L9.78746 3.15516L10.2065 2.57942C10.3717 2.35099 10.5563 2.13497 10.7567 1.93318C10.8582 1.83092 10.9638 1.73231 11.073 1.63759C12.1096 0.730179 13.2155 0.25457 14.3593 0.220151C15.9662 0.179474 17.5857 1.02743 18.5057 2.39794C20.1997 4.92143 18.585 7.98499 18.5202 8.10791C18.5192 8.10982 18.5183 8.1115 18.5183 8.1115C17.8755 9.27549 17.28 9.98577 16.8893 10.4207C15.9189 11.494 13.7228 13.6498 9.78431 16.2188C5.57485 13.4746 3.53944 11.422 2.69188 10.4301C2.13734 9.77926 1.61116 9.03768 1.04087 8.09273C1.02196 8.06456 -0.682611 4.99814 1.06293 2.39794C1.9546 1.06498 3.52054 0.220151 5.07703 0.220151H5.20306ZM2.34605 7.33752C2.34158 7.32986 2.33561 7.31987 2.32823 7.30801L2.33644 7.32161C2.33965 7.32692 2.34285 7.33223 2.34605 7.33752Z" fill="#1F1F1F"/>
                    </svg>
                    <span>Speichern</span>
                </div>
            </div> */}
        </header>
        {authWaitingPresign ? (
            <>
                {galleryLayout === "3col" && (
                    <GalleryGridSkeleton
                        galleryLayout="3col"
                        imageCount={images?.length ?? 0}
                        containerClassName={classes.carouselContainer}
                        scrollRootRef={scrollRootRef}
                    />
                )}
                {galleryLayout === "2col" && (
                    <GalleryGridSkeleton
                        galleryLayout="2col"
                        imageCount={images?.length ?? 0}
                        containerClassName={classes.carouselContainerSmallerScreen}
                        scrollRootRef={scrollRootRef}
                    />
                )}
                {galleryLayout === "1col" && (
                    <GalleryGridSkeleton
                        galleryLayout="1col"
                        imageCount={images?.length ?? 0}
                        containerClassName={classes.carouselContainerSmallestScreen}
                        scrollRootRef={scrollRootRef}
                    />
                )}
            </>
        ) : (
            <>
        {galleryLayout === "3col" && (
        <div className={classes.carouselContainer} ref={scrollRootRef}>
            {[0, 1, 2].map((columnIndex) => (
                <div key={columnIndex} className={classes.imageColumn}>
                    {images.filter((_, index) => index % 3 === columnIndex).map((_, index) => {
                        const gi = index * 3 + columnIndex;
                        return (
                        <GalleryImage
                            key={gi}
                            attemptUrls={attemptUrlsByIndex[gi] || []}
                            globalIndex={gi}
                            scrollRootRef={scrollRootRef}
                            alt={`Studio Image ${gi + 1}`}
                            onImageClick={handleImageClick}
                            className={classes.galleryImage}
                        />
                        );
                    })}
                </div>
            ))}
        </div>
        )}
        {galleryLayout === "2col" && (
        <div className={classes.carouselContainerSmallerScreen} ref={scrollRootRef}>
            {[0, 1].map((columnIndex) => (
                <div key={columnIndex} className={classes.imageColumn}>
                    {images.filter((_, index) => index % 2 === columnIndex).map((_, index) => {
                        const gi = index * 2 + columnIndex;
                        return (
                        <GalleryImage
                            key={gi}
                            attemptUrls={attemptUrlsByIndex[gi] || []}
                            globalIndex={gi}
                            scrollRootRef={scrollRootRef}
                            alt={`Studio Image ${gi + 1}`}
                            onImageClick={handleImageClick}
                            className={classes.galleryImage}
                        />
                        );
                    })}
                </div>
            ))}
        </div>
        )}
        {galleryLayout === "1col" && (
        <div className={classes.carouselContainerSmallestScreen} ref={scrollRootRef}>
            {[0].map((columnIndex) => (
                <div key={columnIndex} className={classes.imageColumn}>
                    {images.filter((_, index) => index % 1 === columnIndex).map((_, index) => {
                        const gi = index * 1 + columnIndex;
                        return (
                        <GalleryImage
                            key={gi}
                            attemptUrls={attemptUrlsByIndex[gi] || []}
                            globalIndex={gi}
                            scrollRootRef={scrollRootRef}
                            alt={`Studio Image ${gi + 1}`}
                            onImageClick={handleImageClick}
                            className={classes.galleryImage}
                        />
                        );
                    })}
                </div>
            ))}
        </div>
        )}
            </>
        )}
        {showCarousel && (
          <StudioCarousel 
            slideAttempts={carouselAttemptUrlsByIndex}
            images={images}
            passthroughImages={cloudFrontPassthroughUrls}
            fallbackImages={s3FallbackUrls}
            startIndex={carouselStartIndex} 
            onClose={() => {
                setShowCarousel(false);
            }}
          />
        )}
    </div>
  );
};

export default StudioGallery;
